'use client';

import {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import type { GentooDisplayLocation } from '@/shared/lib/gentoo';

/**
 * Lead 슬롯 — 오피스견적·상담신청·전화걸기 3-in-1 카드.
 * `estimate` / `consult` / `call` 세 항목의 노출 여부를 개별로 제어.
 * 견적(고정 링크)·전화(고정 tel)는 스펙 고정. 상담은 기본 리드폼 팝업 오픈이며,
 * 페이지별 커스텀 동작이 필요할 때 `consultAction` 으로 override.
 * render 가 지정되면 기본 <LeadButton /> 대신 커스텀 컴포넌트로 교체 (하위 필드 무시).
 */
export type LeadSlot =
  | {
      visible: boolean;
      estimate: boolean;
      consult: boolean;
      call: boolean;
      consultAction?: () => void;
      render?: () => ReactNode;
    }
  | null;

/**
 * ScrollTop 슬롯 — 젠투와 같은 3-상태.
 * `null`(미등록) 은 숨김이 아니라 **기본 노출**이다. 숨기려면 `{ visible: false }`.
 */
export type ScrollTopSlot = { visible: boolean } | null;

/**
 * Zento 슬롯 — visible 이면 렌더러 노출.
 * render 가 지정되면 기본 <ZentoButton /> 대신 커스텀 컴포넌트로 교체.
 *
 * Gentoo SDK boot 파라미터 (`displayLocation`, `itemId`) 도 함께 담아
 * `GentooInitializer` 가 페이지별 컨텍스트로 재부트할 수 있게 한다.
 */
/**
 * Zento 트리거 UI 모드.
 * - `custom` (기본): 프로젝트의 `<ZentoButton />` 사용. SDK 는 `isCustomButton: true`.
 * - `sdk`: SDK 기본 플로팅 버튼 사용. 프로젝트 버튼은 숨김.
 * - `none`: 어떤 버튼도 노출하지 않음. SDK boot 만 진행 (프로액티브 그리팅 검증용).
 */
export type ZentoButtonMode = 'custom' | 'sdk' | 'none';

export type ZentoSlot =
  | {
      visible: boolean;
      render?: () => ReactNode;
      displayLocation?: GentooDisplayLocation;
      itemId?: string;
      buttonMode?: ZentoButtonMode;
    }
  | null;

/**
 * 컨테이너 커스터마이즈 — position/style 을 두 wrapper 별로 독립 override.
 * - leadClassName: LeadButton + ScrollTop 그룹의 wrapper (기본: 세로 중앙 우측)
 * - zentoClassName: Zento 단독 wrapper (기본: 우하단)
 * 기본값은 twMerge 로 덮여 필요한 축만 지정 가능.
 */
export type ContainerSlot = {
  leadClassName?: string;
  zentoClassName?: string;
} | null;

type FloatingLayerContextValue = {
  lead: LeadSlot;
  setLead: (v: LeadSlot) => void;
  scrollTop: ScrollTopSlot;
  setScrollTop: (v: ScrollTopSlot) => void;
  zento: ZentoSlot;
  setZento: (v: ZentoSlot) => void;
  container: ContainerSlot;
  setContainer: (v: ContainerSlot) => void;
  /**
   * 페이지가 화면 하단에 자체 고정 CTA 를 띄우고 있음(예: 블로그 글의 플로팅 CTA).
   * 자리를 두고 다투는 MO 하단 리드 바는 숨기고, 젠투+ScrollTop 스택은 그 CTA 위로 올린다.
   * PC 우측 세로 레일(LeadButton)은 겹치지 않으므로 그대로 노출한다.
   */
  pageBottomCta: boolean;
  setPageBottomCta: (v: boolean) => void;
};

const FloatingLayerContext = createContext<FloatingLayerContextValue>({
  lead: null,
  setLead: () => {},
  scrollTop: null,
  setScrollTop: () => {},
  zento: null,
  setZento: () => {},
  container: null,
  setContainer: () => {},
  pageBottomCta: false,
  setPageBottomCta: () => {},
});

export function FloatingLayerProvider({ children }: PropsWithChildren) {
  const [lead, setLead] = useState<LeadSlot>(null);
  const [scrollTop, setScrollTop] = useState<ScrollTopSlot>(null);
  const [zento, setZento] = useState<ZentoSlot>(null);
  const [container, setContainer] = useState<ContainerSlot>(null);
  const [pageBottomCta, setPageBottomCta] = useState(false);
  return (
    <FloatingLayerContext.Provider
      value={{
        lead,
        setLead,
        scrollTop,
        setScrollTop,
        zento,
        setZento,
        container,
        setContainer,
        pageBottomCta,
        setPageBottomCta,
      }}
    >
      {children}
    </FloatingLayerContext.Provider>
  );
}

export function useFloatingLayer() {
  return useContext(FloatingLayerContext);
}
